import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { GetRoleInterface } from "@type/security.types"
import { rolesService } from "services"
import { AppThunk, RootState } from "store"

export interface RolesStateInterface {
	list: GetRoleInterface[]
	userAccess: any
	loading: boolean
	saving: boolean
	loadingAllowedResources: boolean
}

const initialState: RolesStateInterface = {
	list: [],
	userAccess: [],
	loading: false,
	loadingAllowedResources: false,
	saving: false,
}

const rolesSlice = createSlice({
	name: "roles",
	initialState,
	reducers: {
		fetchingRoles: role => {
			role.loading = true
		},
		fetchingAllowedResources: role => {
			role.loadingAllowedResources = true
		},
		rolesFetched: (role, action: PayloadAction<GetRoleInterface[]>) => {
			role.loading = false
			role.list = action.payload
		},
		rolesFetchingFailed: role => {
			role.loading = false
		},
		allowedResourcesFetchingFailed: role => {
			role.loadingAllowedResources = false
		},
		savingRole: roleData => {
			roleData.saving = true
		},
		roleSaved: (role, action: PayloadAction<GetRoleInterface[]>) => {
			role.saving = false
			role.list = action.payload
		},
		roleSavingFailed: role => {
			role.saving = false
		},
		allowedResourcesFetched: (role, action: PayloadAction<any[]>) => {
			role.loadingAllowedResources = false
			role.userAccess = action.payload
		},
	},
})

//REDUCER
export default rolesSlice.reducer

//ACTIONS
const { fetchingRoles, rolesFetched, rolesFetchingFailed, savingRole, roleSaved, roleSavingFailed } = rolesSlice.actions

const getUserRoles =
	(userId: string): AppThunk =>
	async dispatch => {
		try {
			dispatch(fetchingRoles())
			const { data: roleResponse } = await rolesService.getRoleByUserId(userId)
			dispatch(rolesFetched(roleResponse))
		} catch (error) {
			dispatch(rolesFetchingFailed())
		}
	}

// This is will handle create and update role.
const saveRole =
	(roleData: any, userId: string, cb?: () => void): AppThunk =>
	async dispatch => {
		try {
			dispatch(savingRole())
			const { data: roleResponse } = await rolesService.createRole(roleData, userId)
			dispatch(roleSaved(roleResponse.data))
			cb?.()
		} catch (error) {
			dispatch(roleSavingFailed())
		}
	}

export { getUserRoles, saveRole }

//SELECTORS
const selectRoleState = (state: RootState) => state.security.roles

const isLoadingRoles = () => (state: RootState) => selectRoleState(state).loading
const isRoleSaving = () => (state: RootState) => selectRoleState(state).saving
const selectRoleList = (state: RootState) => state.security.roles.list || []

const selectRoleById = (roleId: string | null) => (state: RootState) =>
	state.security.roles?.list?.find(role => role._id === roleId)

const selectUserAccessRoles = () => (state: RootState) => selectRoleState(state).userAccess

const selectUserAccessModalLoading =
	() =>
	({ security, employee, location, staticData, branch, customers }: RootState) => {
		return (
			security.roles.loading ||
			employee.details.loading ||
			location.details.loading ||
			staticData.SDSuppliers.loading ||
			branch.loading ||
			customers.loading
		)
	}

export {
	selectRoleList,
	selectRoleState,
	selectRoleById,
	isLoadingRoles,
	isRoleSaving,
	selectUserAccessRoles,
	selectUserAccessModalLoading,
}
